import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, Phone, X } from 'lucide-react';
import { company, navigation } from '../data/siteData';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownCloseTimer = useRef(null);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setMobileOpen(false);
      setDropdownOpen(false);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
        setMobileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
      if (dropdownCloseTimer.current) {
        window.clearTimeout(dropdownCloseTimer.current);
      }
    };
  }, []);

  const ydelserLink = useMemo(() => navigation.main.find((item) => item.label === 'Ydelser'), []);
  const isServicesActive = location.pathname.startsWith('/ydelser');

  const openDropdown = () => {
    if (dropdownCloseTimer.current) {
      window.clearTimeout(dropdownCloseTimer.current);
    }
    setDropdownOpen(true);
  };

  const closeDropdown = () => {
    if (dropdownCloseTimer.current) {
      window.clearTimeout(dropdownCloseTimer.current);
    }
    dropdownCloseTimer.current = window.setTimeout(() => {
      setDropdownOpen(false);
    }, 120);
  };

  const navLinkClass = ({ isActive }) =>
    [
      'relative text-sm font-semibold uppercase tracking-[0.16em] transition-colors',
      isActive ? 'text-brand-orange' : 'text-brand-black',
    ].join(' ');

  const activeUnderline = 'after:absolute after:left-0 after:-bottom-2 after:h-0.5 after:w-full after:bg-brand-orange';

  return (
    <header className="sticky top-0 z-50 border-b border-brand-gray-200 bg-white/95 backdrop-blur-sm">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/cc07307d-38f2-4d7e-9eca-ab76959f9adc/rs-byg-logo.png"
            alt="RS BYG Tømrerfirma"
            className="h-12 w-auto object-contain"
          />
        </Link>

        <nav className="hidden min-[1200px]:flex items-center gap-8">
          <NavLink to="/" end className={({ isActive }) => `${navLinkClass({ isActive })} ${isActive ? activeUnderline : ''}`}>
            Forside
          </NavLink>

          <div
            className="relative"
            ref={dropdownRef}
            onMouseEnter={openDropdown}
            onMouseLeave={closeDropdown}
            onFocusCapture={openDropdown}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                closeDropdown();
              }
            }}
          >
            <button
              type="button"
              onClick={() => setDropdownOpen((value) => !value)}
              className={`flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.16em] transition-colors ${isServicesActive ? 'text-brand-orange' : 'text-brand-black'} hover:text-brand-orange`}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              Ydelser
              <ChevronDown className={`h-4 w-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropdownOpen ? (
              <div className="absolute left-0 top-full mt-3 w-60 border border-brand-gray-200 bg-white py-2 shadow-[0_20px_50px_rgba(17,17,17,0.12)]">
                <NavLink
                  to={ydelserLink?.to || '/ydelser'}
                  className="block px-4 py-2 text-sm font-semibold uppercase tracking-[0.12em] text-brand-black transition-colors hover:bg-brand-gray-50 hover:text-brand-orange"
                >
                  Oversigt
                </NavLink>
                <div className="my-2 border-t border-brand-gray-100" />
                {ydelserLink?.children?.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm font-medium transition-colors hover:bg-brand-gray-50 hover:text-brand-orange ${isActive ? 'bg-brand-gray-50 text-brand-orange' : 'text-brand-black'}`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ) : null}
          </div>

          <NavLink to="/galleri" className={({ isActive }) => `${navLinkClass({ isActive })} ${isActive ? activeUnderline : ''}`}>
            Galleri
          </NavLink>
          <NavLink to="/om-rs-byg" className={({ isActive }) => `${navLinkClass({ isActive })} ${isActive ? activeUnderline : ''}`}>
            Om RS Byg
          </NavLink>
          <NavLink to="/kontakt" className={({ isActive }) => `${navLinkClass({ isActive })} ${isActive ? activeUnderline : ''}`}>
            Kontakt
          </NavLink>
        </nav>

        <div className="hidden min-[1200px]:flex items-center gap-4">
          <a href={company.phoneHref} className="flex items-center gap-2 text-sm font-bold text-brand-black transition-colors hover:text-brand-orange whitespace-nowrap">
            <Phone className="h-4 w-4 text-brand-orange" />
            <span className="whitespace-nowrap">{company.phone}</span>
          </a>
          <Link to="/kontakt" className="inline-flex items-center justify-center bg-brand-black px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-orange">
            Få et tilbud
          </Link>
        </div>

        <div className="flex min-[1200px]:hidden items-center gap-3">
          <a href={company.phoneHref} className="inline-flex h-10 w-10 items-center justify-center border border-brand-gray-200 text-brand-black transition-colors hover:text-brand-orange">
            <Phone className="h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((value) => !value)}
            className="inline-flex h-10 w-10 items-center justify-center bg-brand-black text-white transition-colors hover:bg-brand-orange"
            aria-expanded={mobileOpen}
            aria-label="Åbn menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div className="border-t border-brand-gray-200 bg-white min-[1200px]:hidden">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
            <nav className="flex flex-col gap-4">
              <NavLink to="/" end className={({ isActive }) => `border-b border-brand-gray-100 pb-3 text-base font-semibold uppercase tracking-[0.14em] ${isActive ? 'text-brand-orange' : 'text-brand-black'}`}>
                Forside
              </NavLink>

              <div className="border-b border-brand-gray-100 pb-3">
                <div className="mb-3 flex items-center justify-between">
                  <NavLink to={ydelserLink?.to || '/ydelser'} className={({ isActive }) => `text-base font-semibold uppercase tracking-[0.14em] ${isActive || isServicesActive ? 'text-brand-orange' : 'text-brand-black'}`}>
                    Ydelser
                  </NavLink>
                </div>
                <div className="grid gap-2 pl-3 border-l-2 border-brand-gray-200">
                  <NavLink to={ydelserLink?.to || '/ydelser'} className="text-sm font-semibold uppercase tracking-[0.12em] text-brand-black transition-colors hover:text-brand-orange">
                    Oversigt
                  </NavLink>
                  {ydelserLink?.children?.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) => `text-sm font-medium uppercase tracking-[0.12em] transition-colors hover:text-brand-orange ${isActive ? 'text-brand-orange' : 'text-brand-black'}`}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>

              <NavLink to="/galleri" className={({ isActive }) => `border-b border-brand-gray-100 pb-3 text-base font-semibold uppercase tracking-[0.14em] ${isActive ? 'text-brand-orange' : 'text-brand-black'}`}>
                Galleri
              </NavLink>
              <NavLink to="/om-rs-byg" className={({ isActive }) => `border-b border-brand-gray-100 pb-3 text-base font-semibold uppercase tracking-[0.14em] ${isActive ? 'text-brand-orange' : 'text-brand-black'}`}>
                Om RS Byg
              </NavLink>
              <NavLink to="/kontakt" className={({ isActive }) => `border-b border-brand-gray-100 pb-3 text-base font-semibold uppercase tracking-[0.14em] ${isActive ? 'text-brand-orange' : 'text-brand-black'}`}>
                Kontakt
              </NavLink>

              <div className="grid gap-3 pt-2">
                <a href={company.phoneHref} className="inline-flex items-center justify-center border border-brand-gray-200 px-4 py-3 text-sm font-bold whitespace-nowrap transition-colors hover:border-brand-orange hover:text-brand-orange">
                  Ring {company.phone}
                </a>
                <Link to="/kontakt" className="inline-flex items-center justify-center bg-brand-black px-4 py-3 text-sm font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-brand-orange">
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </nav>
          </div>
        </div>
      ) : null}
    </header>
  );
}
